/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import {
  SpecialRequestType,
  type SpecialRequest,
} from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import { Card, Flex, Group, Text, rem, type CardProps } from '@mantine/core';

import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { openContextModal } from '@mantine/modals';
import { memo, useCallback, useMemo } from 'react';
import FragileItemsDelivery from './fragile-item-delivery';
import {
  handleBooleanRequestType,
  handlePerUnitRequestType,
  handleCommissionDistancePercentageRequestType,
  handleLoadingGoodsAction,
} from './generate-action-request';
import InfoTooltip from '@/components/info-tooltip';
import { ModalName } from '@/components/modals';

import ModalWrapper from '@/components/modals/modal-wrapper';
import DoorToDoor from '@/features/booking/components/bike-special-request/door-to-door';
import SendSmsToRecipient from '@/features/booking/components/bike-special-request/send-sms-to-recipient';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = Omit<CardProps, 'children'> & {
  request: SpecialRequest;
  handleChangeRequestParams: (
    id: string,
    value?: string | number | boolean
  ) => void;
  service_requests?: string | number | boolean;
  isMobile?: boolean;
};

const SpecialRequestItem = memo(function SpecialRequestItem({
  request,
  service_requests,
  handleChangeRequestParams,
  isMobile,
}: Props) {
  const { selectedService, estimatedData, setServiceRequest, serviceRequests } =
    useBookingStore((state) => state);
  const localeSuffix = useLocaleSuffix();
  const paths = useLocationStore.use.paths();

  // Calculate fee with inline useMemo
  const estimatedFee = useMemo(() => {
    if (
      request.type === SpecialRequestType.CommissionDistancePercentage &&
      request.price &&
      estimatedData?.data
    ) {
      // Calculate the percentage fee
      const distanceFee =
        estimatedData?.data?.partner_distance_fee ||
        estimatedData?.data?.distance_fee ||
        0;
      if (!distanceFee) return 0;
      const baseFee =
        distanceFee - (estimatedData.data.cod_commission_price ?? 0);
      return baseFee > 0
        ? Math.round((Number(request.price) * baseFee) / 1000) * 1000
        : 0;
    }

    // Handle loading goods request (MULTI_OPTIONS)
    if (request.type === ('MULTI_OPTIONS' as any)) {
      const loadingGoodsRequest = estimatedData?.data?.requests?.filter(
        (item) => item._id === request._id
      );
      if (loadingGoodsRequest?.length) {
        let fee = 0;
        loadingGoodsRequest.forEach((item) => {
          fee += item.price ?? 0;
        });
        return fee;
      }
    }
    return 0;
  }, [estimatedData, request]);

  const [
    openedDoorToDoorModal,
    { open: openDoorToDoorModal, close: closeDoorToDoorModal },
  ] = useDisclosure(false);

  const [
    openedModalFragile,
    { open: openModalFragile, close: closeModalFragile },
  ] = useDisclosure(false);

  const [
    openedModalSendSmsToRecipient,
    { open: openModalSendSmsToRecipient, close: closeModalSendSmsToRecipient },
  ] = useDisclosure(false);

  const isSelected = useMemo(() => {
    if (!selectedService) return false;
    return selectedService._id === request._id;
  }, [selectedService, request._id]);
  const openModalLoadingGoods = useCallback(
    (request) => {
      openContextModal({
        modal: ModalName.LoadingGoods,
        modalId: ModalName.LoadingGoods,
        innerProps: {
          request,
        },
        p: 0,
        zIndex: 10,
        size: rem(718),
        styles: {
          root: { zIndex: 10 },
          body: {
            position: 'relative',
            padding: 0,
          },
          content: {
            backgroundColor: isMobile
              ? 'var(--mantine-color-neutral-0)'
              : 'white',
          },
        },
        closeOnClickOutside: false,
        closeOnEscape: false,
        withCloseButton: false,
        centered: true,
        fullScreen: Boolean(isMobile),
      });
    },
    [isMobile]
  );

  const handleClickAction = useCallback(
    (e: React.MouseEvent, id: string, value?: string | number | boolean) => {
      e.stopPropagation();

      const handleRequest = (openModal: () => void) => {
        if (serviceRequests && id in serviceRequests) {
          const param = { ...serviceRequests };

          delete param[id];
          setServiceRequest(param);
        } else {
          openModal();
        }
      };
      if (request?.name === 'Door-to-door') {
        handleRequest(openDoorToDoorModal);
        return;
      }

      switch (request.group_id) {
        case 'LOADING-OPTIONS': {
          const param = { ...serviceRequests };
          if (param[id]) {
            delete param[id];
            setServiceRequest(param);
          } else {
            openModalLoadingGoods(request);
          }
          break;
        }
        case 'FRAGILE':
          handleRequest(openModalFragile);
          break;
        case 'D2D':
          handleRequest(openDoorToDoorModal);
          break;
        case 'SMS':
          if (paths.length > 2) {
            openContextModal({
              modal: ModalName.SendSmsToRecipientReject,
              innerProps: {},
              closeOnClickOutside: false,
              closeOnEscape: false,
              withCloseButton: false,
              centered: true,
            });
          } else {
            handleRequest(openModalSendSmsToRecipient);
          }
          break;
        default:
          handleChangeRequestParams(id, value);
      }
    },
    [
      request,
      serviceRequests,
      setServiceRequest,
      openModalFragile,
      openModalSendSmsToRecipient,
      paths,
      handleChangeRequestParams,
      request._id,
      request.group_id,
    ]
  );

  if (
    request &&
    request?.group_id === 'FRAGILE' &&
    Number(request?.max_distance) < Number(estimatedData?.data?.distance) * 1000
  ) {
    return null;
  }

  if (!request?._id.includes(request?.service_id)) {
    return null;
  }

  return (
    <>
      <Card
        key={request._id}
        withBorder={isMobile}
        c={'primary'}
        // bg={service_requests ? '#FFFAF8' : 'transparent'}
        radius={8}
        p={0}
        h="auto"
        mih={48}
        className={`transition-all duration-300 ease-in-out hover:bg-[#fcfcfc] ${
          isSelected
            ? 'border-2 border-primary-60 bg-white'
            : service_requests
              ? 'border-primary-60'
              : 'border-neutral-20'
        } `}
        style={{
          background: service_requests ? 'var(--mantine-color-primary-0)' : '',
          borderColor: service_requests ? 'var(--mantine-color-primary-6)' : '',
        }}
      >
        <Group
          wrap="nowrap"
          gap={isMobile ? 0 : 'sm'}
          align="center"
          p={isMobile ? 12 : 8}
          w="100%"
        >
          {/* <RequestAvatar request={request} localeSuffix={localeSuffix} /> */}
          <RequestInfo
            request={request}
            localeSuffix={localeSuffix}
            // estimatedFee={estimatedFee}
            service_requests={service_requests}
            handleClickAction={handleClickAction}
          />
          <ActionButton
            estimatedFee={estimatedFee}
            request={request}
            service_requests={service_requests}
            handleClickAction={handleClickAction}
          />
        </Group>
      </Card>

      <ModalWrapper
        title={`${request[`name_${localeSuffix}`]}`}
        isOpen={openedDoorToDoorModal}
        onClose={closeDoorToDoorModal}
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
      >
        <DoorToDoor request={request} onCloseModal={closeDoorToDoorModal} />
      </ModalWrapper>

      <ModalWrapper
        title={`${request[`name_${localeSuffix}`]}`}
        isOpen={openedModalFragile}
        onClose={closeModalFragile}
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
          bodyStyle: { padding: rem(0) },
        }}
      >
        <FragileItemsDelivery
          request={request}
          onCloseModal={closeModalFragile}
        />
      </ModalWrapper>

      <ModalWrapper
        title={`${request[`name_${localeSuffix}`]}`}
        isOpen={openedModalSendSmsToRecipient}
        onClose={closeModalSendSmsToRecipient}
        optionStyle={{
          headerStyle: {
            minHeight: rem(56),
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
      >
        <SendSmsToRecipient
          request={request}
          onCloseModal={closeModalSendSmsToRecipient}
        />
      </ModalWrapper>
    </>
  );
});

const RequestInfo = ({
  request,
  localeSuffix,
  // estimatedFee,
  service_requests,
  handleClickAction,
}) => (
  <Group
    gap={4}
    w={'100%'}
    style={{
      flex: '1 1 auto !important',
      whiteSpace: 'pre-line',
      overflow: 'hidden',
    }}
    justify="space-between"
  >
    <RequestTitle
      request={request}
      localeSuffix={localeSuffix}
      service_requests={service_requests}
      handleClickAction={handleClickAction}
    />
    {/* <PriceInfo
      request={request}
      estimatedFeeestimatedFee={estimatedFee}
      service_requests={service_requests}
    /> */}
  </Group>
);

const RequestTitle = ({
  service_requests,
  request,
  localeSuffix,
  handleClickAction,
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  return (
    <Group
      wrap="nowrap"
      align="flex-start"
      gap={4}
      style={{ flex: '1 1 auto !important' }}
    >
      <Text lineClamp={1} fw={700} size={isMobile ? 'sm' : 'md'} c="neutral.7">
        {request[`name_${localeSuffix}`]}
      </Text>
      <InfoTooltip
        multiline
        maw={300}
        className="self-center"
        onClick={() =>
          openSpecialRequestDetailModal(
            service_requests,
            request,
            isMobile,
            handleClickAction
          )
        }
        label={getRequestDescription(request, localeSuffix)}
      />
    </Group>
  );
};

// const PriceInfo = ({ request, estimatedFee, service_requests }) => (
//   <>
//     {renderDistanceFee(estimatedFee, service_requests)}
//     {renderPrice(request, service_requests)}
//   </>
// );

const ActionButton = ({
  request,
  service_requests,
  handleClickAction,
  estimatedFee,
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');

  return (
    <Flex
      style={{
        flex: 1,
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      {renderActionRequestButton(
        isMobile,
        request,
        service_requests,
        handleClickAction,
        estimatedFee
      )}
    </Flex>
  );
};

// Helper functions
const openSpecialRequestDetailModal = (
  service_requests,
  request,
  fullscreen,
  handleClickAction
) => {
  openContextModal({
    modal: ModalName.SpecialRequestDetail,
    innerProps: {
      request,
      service_request: service_requests,
      handleClickAction: handleClickAction,
    },
    h: 'fit-content',
    closeOnClickOutside: false,
    closeOnEscape: false,
    withCloseButton: false,
    centered: true,
    // scrollAreaComponent: ScrollArea.Autosize,
    maw: rem(440),
    w: '100%',
    styles: {
      root: { zIndex: 1001 },
      inner: {
        padding: 0,
        overflow: 'hidden',
      },
      content: {
        backgroundColor: 'white',
        overflow: 'hidden',
        margin: rem(16),
        maxWidth: `calc(100% - ${rem(32)})`,
      },
      body: {
        position: 'relative',
        padding: rem(16),
        paddingTop: rem(48),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'var(--stack-align, stretch)',
        justifyContent: 'var(--stack-justify, flex-start)',
        maxHeight:
          'var(--modal-content-max-height, calc(100dvh - var(--modal-y-offset) * 2))',
      },
    },
  });
};

const getRequestDescription = (request, localeSuffix) =>
  request[`description_${localeSuffix}`] || request[`name_${localeSuffix}`];

const renderActionRequestButton = (
  isMobile,
  request,
  service_requests,
  handleClickAction,
  estimatedFee
) => {
  const param = Number(service_requests ?? 0);
  switch (request.type) {
    case SpecialRequestType.CodInsurance:
      return null;
    case SpecialRequestType.PerUnit:
      return handlePerUnitRequestType(
        isMobile,
        request,
        param,
        handleClickAction
      );
    case SpecialRequestType.CommissionDistancePercentage:
      return handleCommissionDistancePercentageRequestType(
        isMobile,
        request,
        param,
        estimatedFee,
        handleClickAction
      );
    case 'MULTI_OPTIONS':
      return handleLoadingGoodsAction(
        isMobile,
        request,
        param,
        estimatedFee,
        handleClickAction
      );
    default:
      return handleBooleanRequestType(
        isMobile,
        request,
        service_requests,
        handleClickAction
      );
  }
};

export default SpecialRequestItem;
